/**
 * AI 개선 의견 후보 소비 — 작성 페이지 측 트리 감지 (커스텀 모달 위젯 P3).
 *
 * 구 SMS 작성 진입 플로우(ReportTypeSelectModal → sessionStorage → 작성 페이지)를 composed 작성
 * 페이지의 카탈로그 resolve로 대체한다. 파트별 관례 키(COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS)를
 * optionsSource로 가진 sortableSelect가 있으면, 페이지가 후보 rows를 만들어 resolvedRows 채널로 주입한다.
 *
 * rows 변환기 자체(improvementCandidatesToRows)는 shared 정본 — admin 에디터 캔버스도 같은 rows를 써야
 * 최종 작성 화면과 동일하게 보이기 때문(2026-07-16). 여기 함수들은 위젯 트리를 훑어 파트를 감지하는
 * host 로직으로, rc(effectiveOptionsSources)에 의존해 shared로 못 올린다(의존 방향: rc → shared).
 *
 * 2026-07-26 web → rc 승격: admin 시뮬레이션도 web 배포판과 같은 파트 감지·진입 입력 키 규약을
 * 소비해야 하므로 rc 정본으로 이동(구 web improvementCandidates.ts — web은 재수출층만 유지).
 */
import type { ComposerWidget, ImprovementCandidatePart } from '@safetyhub/shared';
import { COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS } from '@safetyhub/shared';
import { effectiveOptionsSources } from './composerWidgetTreeRender';

/** 소스 키(improvement-candidates:guidance 등) → 파트 식별자. 매핑 없으면 null. */
function sourceKeyToPart(key: string): ImprovementCandidatePart | null {
  const part = (Object.keys(COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS) as ImprovementCandidatePart[]).find(
    (p) => COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS[p] === key,
  );
  return part ?? null;
}

/**
 * 템플릿 위젯 트리에서 사용 중인 AI 후보 파트 전부 수집(userInput 메타 여부 무관, 중복 제거).
 * 진입 전 사전 생성 대기용(2026-07-16 사용자 확정 — 캐시가 비어 생성이 필요한 경우도 타입 선택
 * 모달 로딩으로 완료를 기다렸다 진입, 구 SMS 사전 생성 동형). group/modalTrigger/repeat children 재귀
 * (repeat 하강은 P3 — 행 템플릿 안 sortableSelect도 파트 감지, rc collectSortableSelectOptionSources와
 * 동일 규칙. 파트 감지는 선언(트리) 기준이라 행 개수와 무관).
 */
export function collectImprovementParts(
  tree: ComposerWidget[] | undefined,
): ImprovementCandidatePart[] {
  if (!tree?.length) return [];
  const parts: ImprovementCandidatePart[] = [];
  const visit = (widgets: ComposerWidget[]): void => {
    for (const w of widgets) {
      if (w.kind === 'input' && w.widget === 'sortableSelect') {
        for (const key of effectiveOptionsSources(w)) {
          const part = sourceKeyToPart(key);
          if (part && !parts.includes(part)) parts.push(part);
        }
      }
      if (w.kind === 'group') visit(w.children);
      else if (w.kind === 'modalTrigger') visit(w.children);
      else if (w.kind === 'repeat') visit(w.children);
      // branch: 투명 컨테이너 — 슬롯 children의 sortableSelect 소스도 수집(전 슬롯 하강).
      else if (w.kind === 'branch') for (const slot of w.branches) visit(slot.children);
    }
  };
  visit(tree);
  return parts;
}

/** 진입 전 입력 sessionStorage 키 — 템플릿 키별. new-composed가 최초 AI 요청 시 1회 소비(읽고 제거). */
export function composedEntryGuidanceKey(templateKey: string): string {
  return `safetyhub-composed-entry-guidance:${templateKey}`;
}
